// src/pages/financials/KpiPanel.tsx
//
// COGS & Expense KPI panel (recipe qbd-kpi-panel, flag financials.kpipanel,
// agent 3.20.0+) — the PBI "COGS & Exp KPI" page: Income, COGS, Payroll and
// Overhead as $ and % of income for the same month (or YTD) across years,
// plus net income vs budget for the current year. Rendered inside Overview.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Gauge, RefreshCw, AlertTriangle } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdKpiYear } from '../../types';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const pad = (n: number) => String(n).padStart(2, '0');
const lastCompleteMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };

const pill = (on: boolean) => `px-2 py-1 text-[11px] rounded-md border transition-all ${
  on ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

// For a cost ratio, lower than last year is good (green).
function Trend({ now, prev, cost }: { now: number; prev?: number; cost: boolean }) {
  if (prev == null) return null;
  const d = now - prev;
  if (Math.abs(d) < 0.05) return <span className="text-slate-500 text-[10px] ml-1">±0</span>;
  const good = cost ? d < 0 : d > 0;
  return <span className={`text-[10px] ml-1 ${good ? 'text-emerald-400' : 'text-red-400'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d).toFixed(1)}</span>;
}

const METRICS: { key: 'income' | 'cogs' | 'payroll' | 'overhead' | 'netIncome'; pct?: keyof QbdKpiYear; label: string; cost: boolean }[] = [
  { key: 'income',    label: 'Income',            cost: false },
  { key: 'cogs',      pct: 'cogsPct',     label: 'COGS',              cost: true },
  { key: 'payroll',   pct: 'payrollPct',  label: 'Payroll Expenses',  cost: true },
  { key: 'overhead',  pct: 'overheadPct', label: 'Overhead Expenses', cost: true },
  { key: 'netIncome', pct: 'netPct',      label: 'Net Income',        cost: false },
];

export function KpiPanel() {
  const [month, setMonth] = useState(lastCompleteMonth);
  const [period, setPeriod] = useState<'month' | 'ytd'>('month');

  const q = useQuery({
    queryKey: ['qbd', 'kpi-panel', month, period],
    queryFn: () => api.getQbdKpiPanel({ month, period }),
    staleTime: 60_000,
    retry: 1,
  });
  const years = q.data?.years ?? [];
  const cur = years[0];

  return (
    <section className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Gauge size={16} /> COGS &amp; Expense KPIs</h3>
          <p className="text-[11px] text-slate-500">Same {period === 'ytd' ? 'year-to-date span' : 'month'} across years · % of income</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)}
            className="px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white [color-scheme:dark]" />
          <button onClick={() => setPeriod('month')} className={pill(period === 'month')}>Month</button>
          <button onClick={() => setPeriod('ytd')} className={pill(period === 'ytd')}>YTD</button>
          <button onClick={() => q.refetch()} disabled={q.isLoading}
            className="p-1.5 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={q.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {q.error && (
        <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
          <AlertTriangle size={14} /> Couldn't load KPIs: {(q.error as Error).message}
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
      ) : cur && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Metric</th>
                  {years.map(y => (
                    <th key={y.year} colSpan={2} className="px-3 py-2 text-right text-slate-300 font-medium whitespace-nowrap"
                      title={`${y.dateFrom} → ${y.dateTo}`}>{y.year}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map(mt => (
                  <tr key={mt.key} className={`border-b border-slate-800/50 ${mt.key === 'netIncome' ? 'font-semibold' : ''}`}>
                    <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{mt.label}</td>
                    {years.map((y, i) => {
                      const empty = y.income === 0 && y[mt.key] === 0;
                      const pct = mt.pct ? (y[mt.pct] as number) : null;
                      const prev = years[i + 1];
                      return [
                        <td key={`${y.year}-v`} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${y[mt.key] < 0 ? 'text-red-400' : 'text-white'}`}>
                          {empty ? '–' : fmtCur(y[mt.key])}
                        </td>,
                        <td key={`${y.year}-p`} className="px-2 py-2 text-right tabular-nums whitespace-nowrap text-slate-400 w-20">
                          {pct == null || empty ? '' : <>{pct.toFixed(1)}%{prev && prev.income !== 0 && <Trend now={pct} prev={prev[mt.pct!] as number} cost={mt.cost} />}</>}
                        </td>,
                      ];
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cur.budgetNet != null ? (
            <div className="flex flex-wrap gap-4 text-xs text-slate-400">
              <span>Budget net income {cur.year}: <span className="text-white tabular-nums">{fmtCur(cur.budgetNet)}</span></span>
              <span>Actual vs budget:{' '}
                <span className={`tabular-nums ${cur.netIncome >= cur.budgetNet ? 'text-emerald-400' : 'text-red-400'}`}>
                  {cur.netIncome - cur.budgetNet >= 0 ? '+' : '−'}{fmtCur(Math.abs(cur.netIncome - cur.budgetNet))}
                </span>
              </span>
              {cur.budgetIncome != null && <span>Budget income: <span className="text-white tabular-nums">{fmtCur(cur.budgetIncome)}</span></span>}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500">No budget for {period === 'ytd' ? 'this span' : month} — set one up under Budget.</div>
          )}
          <div className="text-[11px] text-slate-500">Payroll = expense accounts named payroll / wages / salaries; overhead = all other expenses.</div>
        </>
      )}
    </section>
  );
}
